"use client";

import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { HiArrowRight } from "react-icons/hi";
import { RiUserSearchLine } from "react-icons/ri";
import { MdOutlineScreenSearchDesktop } from "react-icons/md";
import { useTranslations } from "next-intl";
import { SELF_ADS_COLORS } from "./colors";
import { KnootLogo2026 } from "@/_images/svgs/chakra-icons/Logo";

const COLORS = SELF_ADS_COLORS;

interface RoleCardProps {
  icon: IconType;
  label: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaVariant: "solid" | "outline";
  onClick?: () => void;
}

function RoleCard({
  icon,
  label,
  title,
  description,
  ctaLabel,
  ctaVariant,
  onClick,
}: RoleCardProps) {
  const buttonVariants =
    ctaVariant === "solid"
      ? {
          bg: COLORS.accent,
          color: "white",
          _hover: { bg: "primary.600" },
          shadow: "shadow-primary-xl",
        }
      : {
          variant: "outline" as const,
          bg: "transparent",
          borderColor: "whiteAlpha.400",
          color: "white",
          _hover: { bg: "whiteAlpha.100", borderColor: "whiteAlpha.600" },
        };
  return (
    <Flex
      flex={1}
      direction="column"
      gap={4}
      p={{ base: 5, md: 6 }}
      bg={COLORS.cardBg}
      border="1px solid"
      borderColor={COLORS.cardBorder}
      rounded="2xl"
    >
      <Center
        boxSize={10}
        rounded="lg"
        bg={COLORS.accentBg}
        color={COLORS.accent}
        flexShrink={0}
      >
        <Icon as={icon} boxSize={6} color="primary.400" />
      </Center>

      <VStack align="stretch" gap={2} flex={1}>
        <Text
          textStyle="xs"
          fontWeight="bold"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color={COLORS.label}
        >
          {label}
        </Text>
        <Text
          textStyle={{ base: "md", md: "xl" }}
          fontWeight="extrabold"
          color={COLORS.title}
          lineHeight="short"
        >
          {title}
        </Text>
        <Text textStyle="sm" color={COLORS.body} lineHeight="tall" mt={2}>
          {description}
        </Text>
      </VStack>

      <Button
        mt={2}
        w="fit-content"
        size="md"
        rounded="lg"
        onClick={onClick}
        {...buttonVariants}
      >
        {ctaLabel}
        <Icon as={HiArrowRight} boxSize={4} />
      </Button>
    </Flex>
  );
}

export interface SelfAds2OptionProps extends BoxProps {
  onEmployerClick?: () => void;
  onCandidateClick?: () => void;
}

export function SelfAds2Option({
  onEmployerClick,
  onCandidateClick,
  ...boxProps
}: SelfAds2OptionProps) {
  const t = useTranslations("self_ads.option");

  return (
    <Box
      bg={COLORS.shellBg}
      mt={1}
      rounded="3xl"
      px={{ base: 5, md: 8 }}
      py={{ base: 8, md: 10 }}
      {...boxProps}
    >
      <Flex gap={2} direction="column" align="center" mt="-70px">
        <Box
          bgColor="primary.400/10"
          p={3}
          rounded="3xl"
          shadow={COLORS.accentShadow}
          mb={4}
        >
          <KnootLogo2026 boxSize={12} />
        </Box>
        <VStack gap={2} textAlign="center" mb={{ base: 6, md: 8 }}>
          <Text
            textStyle={{ base: "lg", md: "xl" }}
            fontWeight="medium"
            color={COLORS.title}
            lineHeight="short"
            fontFamily="heading"
          >
            {t("headline")}
          </Text>
          <Text textStyle="md" color={COLORS.body} maxW="520px">
            {t("subhead")}
          </Text>
        </VStack>
      </Flex>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 4, md: 5 }}
        align="stretch"
      >
        <RoleCard
          icon={RiUserSearchLine}
          label={t("employer.label")}
          title={t("employer.title")}
          description={t("employer.description")}
          ctaLabel={t("employer.cta")}
          ctaVariant="solid"
          onClick={onEmployerClick}
        />
        <RoleCard
          icon={MdOutlineScreenSearchDesktop}
          label={t("candidate.label")}
          title={t("candidate.title")}
          description={t("candidate.description")}
          ctaLabel={t("candidate.cta")}
          ctaVariant="outline"
          onClick={onCandidateClick}
        />
      </Flex>
    </Box>
  );
}
